import cx from 'classnames'
import { Avatar, type AvatarSubject } from '@/components/Avatar'
import { Dropdown, DropdownItem } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import { IconButton } from '@/components/IconButton'
import { Link } from '@/components/Link'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import { Spinner } from '@/components/Spinner'
import { Tooltip } from '@/components/Tooltip'
import { LegendPill } from '@/features/legends/components/LegendPill'
import {
  LegendStatusLabel,
  VersionChip,
} from '@/features/legends/components/LegendStatus'
import type { LegendStatus } from '@/features/legends/legend-status'
import styles from './LegendCard.module.css'

export interface LegendCardProps {
  to: string
  name: string
  description: string
  serviceType: string | null
  status: LegendStatus
  version: number
  /**
   * The status of the version, for its chip.
   */
  versionStatus: LegendStatus
  hasUnpublishedChanges: boolean
  isInactive: boolean
  /**
   * When the version was published, or last changed.
   */
  dateLabel: string
  updatedBy: AvatarSubject | null
  /**
   * Whether the edit and (de)activate actions show.
   */
  canModify: boolean
  isToggleActivationPending: boolean
  onEdit: () => unknown
  onToggleActivation: () => unknown
  testId?: string
}

/**
 * A legend in the legend list.
 */
export function LegendCard({
  to,
  name,
  description,
  serviceType,
  status,
  version,
  versionStatus,
  hasUnpublishedChanges,
  isInactive,
  dateLabel,
  updatedBy,
  canModify,
  isToggleActivationPending,
  onEdit,
  onToggleActivation,
  testId,
}: LegendCardProps) {
  return (
    <div
      className={cx(styles.root, isInactive && styles.inactive)}
      data-test={testId}
    >
      <Link to={to} viewTransition className={styles.link}>
        <div className={styles.top}>
          <LegendStatusLabel
            status={status}
            hasUnpublishedChanges={hasUnpublishedChanges}
          />
          <VersionChip version={version} status={versionStatus} isSubtle />
        </div>
        <div className={styles.body}>
          <span className={styles.name} data-test={testId && `${testId}-name`}>
            {name || 'Untitled legend'}
          </span>
          {description && (
            <span className={styles.description}>{description}</span>
          )}
          {serviceType && (
            <LegendPill className={styles.serviceType}>
              {serviceType}
            </LegendPill>
          )}
        </div>
      </Link>
      <div className={styles.footer}>
        {updatedBy && (
          <Tooltip content={updatedBy.displayName}>
            <span className={styles.avatar}>
              <Avatar size={18} subject={updatedBy} />
              {updatedBy.displayName && (
                <span className="sr-only">
                  Updated by {updatedBy.displayName}
                </span>
              )}
            </span>
          </Tooltip>
        )}
        <span className={styles.info}>{dateLabel}</span>
        {canModify &&
          (isToggleActivationPending ? (
            <span className={styles.spinner}>
              <Spinner size={14} />
            </span>
          ) : (
            <Dropdown
              autoDismiss
              position="bottom-end"
              testId={testId && `${testId}-menu`}
              button={
                <IconButton name="more" label="Legend actions" size={16} />
              }
            >
              <DropdownItem
                icon={<Icon name="edit-outlined" />}
                onPress={onEdit}
                testId={testId && `${testId}-edit`}
              >
                Edit
              </DropdownItem>
              <DropdownItem
                icon={<Icon name="power" />}
                onPress={onToggleActivation}
                testId={testId && `${testId}-toggle-activation`}
              >
                {isInactive ? 'Activate' : 'Deactivate'}
              </DropdownItem>
            </Dropdown>
          ))}
      </div>
    </div>
  )
}

/**
 * A placeholder for a {@link LegendCard} still loading. Render it inside a
 * `SkeletonList`.
 */
export function LegendCardSkeleton() {
  const { vary } = useSkeletonListContext()
  return (
    <div className={cx(styles.root, styles.skeleton)} aria-hidden>
      <div className={styles.top}>
        <Skeleton width={64} />
        <Skeleton variant="rect" width={28} height={20} />
      </div>
      <div className={styles.body}>
        <span className={styles.name}>
          <Skeleton width={vary(150)} />
        </span>
        <span className={styles.description}>
          <Skeleton width={vary(190)} />
        </span>
      </div>
      <div className={styles.footer}>
        <Skeleton block variant="circle" width={18} height={18} />
        <span className={styles.info}>
          <Skeleton width={vary(120)} />
        </span>
      </div>
    </div>
  )
}
